import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import createPaymentProcessor from '@/shared/api/orders/paymentProcessors/createPaymentProcessor';
import getSupportedProcessorKeys from '@/shared/api/orders/paymentProcessors/getSupportedProcessorKeys';
import updatePaymentProcessor from '@/shared/api/orders/paymentProcessors/updatePaymentProcessor';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import { AddPaymentProcessor } from '@/shared/types/order/paymentProcessor.type';
import SupportedPaymentProcessor from '@/shared/types/order/supportedPaymentProcessor.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const usePaymentProcessorsOperations = ({
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const { t } = useTranslation(['paymentProcessors', 'common']);

  const redirectLogin = useRedirectLogin();

  const [supportedPaymentProcessors, setSupportedPaymentProcessors] = useState<
    SupportedPaymentProcessor[] | null
  >(null);

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const onAddPaymentProcessor = async (processor: AddPaymentProcessor) => {
    onUpdateDialogState(DialogName.AddPaymentProcessor, { loading: true });
    onUpdateProgressDialog({
      title: t('paymentProcessors:addingPaymentProcessor'),
      open: true,
    });

    const response = await createPaymentProcessor({
      body: processor,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('paymentProcessors:paymentProcessorAdded'),
        severity: 'success',
      });
      onUpdateDialogState(DialogName.AddPaymentProcessor, {
        loading: false,
        open: false,
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.AddPaymentProcessor, {
        loading: false,
        errors: [response.msg],
      });
    }
  };

  const onUpdatePaymentProcessor = async (
    processor: AddPaymentProcessor,
    processorId: string,
  ) => {
    onUpdateDialogState(DialogName.EditPaymentProcessor, { loading: true });
    onUpdateProgressDialog({
      title: t('paymentProcessors:updatingPaymentProcessor'),
      open: true,
    });

    const response = await updatePaymentProcessor({
      processorId,
      body: processor,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('paymentProcessors:paymentProcessorUpdated'),
        severity: 'success',
      });
      onUpdateDialogState(DialogName.EditPaymentProcessor, {
        loading: false,
        open: false,
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.EditPaymentProcessor, {
        loading: false,
        errors: [response.msg],
      });
    }
  };

  useEffect(() => {
    const fetchSupportedPaymentProcessors = async () => {
      const response = await getSupportedProcessorKeys({
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        setSupportedPaymentProcessors(response.data);
      }
    };

    void fetchSupportedPaymentProcessors();
  }, [redirectLogin]);

  return {
    supportedPaymentProcessors,
    refreshTableIndex,
    onAddPaymentProcessor,
    onUpdatePaymentProcessor,
  };
};

export default usePaymentProcessorsOperations;
